//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import 'draft-js/dist/Draft.css';

import Editor from '@draft-js-plugins/editor';
import {
  ContentBlock,
  ContentState,
  DraftHandleValue,
  EditorState,
  getDefaultKeyBinding,
} from 'draft-js';
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import { CaptionModificationType } from '@apis/scheduler-generative-ai/schedulerGenerativeAiModel';
import { usePopover } from '@components/molecules/popover/usePopover';
import { DecoratedText } from '@components/molecules/smart-input/DecoratedText';
import {
  ACTION_BAR_HEIGHT,
  SmartInputActionBar,
} from '@components/molecules/smart-input/SmartInputActionBar';
import {
  ISmartInputContext,
  SmartInputContextProvider,
} from '@components/molecules/smart-input/SmartInputContext';
import {
  mentionsSuggestionsDecorators,
  recommendedHashtagsDecorators,
} from '@components/molecules/smart-input/decoratorStrategies';
import { GenerativeAiCaptionModal } from '@components/molecules/smart-input/generative-ai-caption/GenerativeAiCaptionModal';
import { LinkShorteningModal } from '@components/molecules/smart-input/link-shortening/LinkShorteningModal';
import { MentionsSuggestionsDropdown } from '@components/molecules/smart-input/mentions/MentionsSuggestionsDropdown';
import {
  getIsCursorAfterAtSymbol,
  getIsCursorInMention,
} from '@components/molecules/smart-input/mentions/mentionsUtils';
import { HashtagDropdown } from '@components/molecules/smart-input/recommended-hashtags/HashtagDropdown';
import {
  DropdownValue,
  ILinkShorteningModalConfiguration,
  IMentionsSuggestionsQuery,
  ISmartInputEmojiPickerConfiguration,
  ISmartInputGenerativeAiCaptionConfiguration,
  ISmartInputGenerativeAiSocialInboxResponseConfiguration,
  ISmartInputLinkShorteningConfiguration,
  ISmartInputMentionsConfiguration,
  ISmartInputRecommendedCaptionConfiguration,
  ISmartInputRecommendedHashtagsConfiguration,
  ISmartInputShowroomLinksConfiguration,
  ShowroomLinkCallback,
} from '@components/molecules/smart-input/smartInputModel';
import { KEYS } from '@constants/keyboard';
import { useOutsideClick } from '@hooks/useOutsideClick';
import { SocialNetwork } from '@models/SocialNetwork';
import { isDefined } from '@utils/filterPredicates';
import { getRgbaForThemeHex } from '@utils/getRgbaForThemeHex';
import { keyEquals } from '@utils/keyboardUtils';
import {
  SocialTextEntityType,
  SocialTextParseMethod,
} from '@utils/social-text-utils/socialTextUtilsModel';
import { parseTwitterText } from '@utils/social-text-utils/twitterTextUtils';

interface Props {
  className?: string;
  emojiPicker?: ISmartInputEmojiPickerConfiguration;
  generativeAiCaption?: ISmartInputGenerativeAiCaptionConfiguration;
  generativeAiResponse?: ISmartInputGenerativeAiSocialInboxResponseConfiguration;
  hasError?: boolean;
  isDisabled?: boolean;
  linkShortening?: ISmartInputLinkShorteningConfiguration;
  mentions?: ISmartInputMentionsConfiguration;
  numRows?: number;
  onChange: (val: string) => void;
  handleFocus?: () => void;
  handleBlur?: (e?: React.FocusEvent<HTMLInputElement>) => void;
  handleEnterKeyDown?: () => void;
  placeholder?: string;
  recommendedCaption?: ISmartInputRecommendedCaptionConfiguration;
  recommendedHashtags?: ISmartInputRecommendedHashtagsConfiguration;
  showroomLinks?: ISmartInputShowroomLinksConfiguration;
  socialTextParseMethod?: SocialTextParseMethod;
  trackingIdPrefix?: string;
  value?: string;
}

/**
 * Using this as a workaround to a Storybook bug related to parsing default values
 *
 * related issues:
 * - https://github.com/storybookjs/storybook/issues/12455
 * - https://github.com/storybookjs/storybook/issues/12098
 * */

const defaultEmojiPickerConfig: ISmartInputEmojiPickerConfiguration = {
  isEnabled: true,
};
const defaultGenerativeAiCaptionConfig: ISmartInputGenerativeAiCaptionConfiguration =
  {
    isEnabled: false,
    isEnabledForPlanTier: false,
    isModalTriggerEnabled: false,
    getGeneratedCaption: () => Promise.resolve(undefined),
  };
const defaultGenerativeAiResponseConfig: ISmartInputGenerativeAiSocialInboxResponseConfiguration =
  {
    isAiGenerativeResponseLoading: false,
    isEnabled: false,
    shouldDisableMenu: false,
    dropdownConfig: undefined,
    getGeneratedResponse: (_value: DropdownValue) => Promise.resolve(''),
  };

const defaultAtMentionsConfig: ISmartInputMentionsConfiguration = {
  isEnabled: false,
  getMentionsSuggestions: (_query: string) => Promise.resolve([]),
};
const defaultRecommendedCaptionConfig: ISmartInputRecommendedCaptionConfiguration =
  {
    getRecommendedCaption: (_value: CaptionModificationType | undefined) =>
      Promise.resolve(''),
    isEnabled: false,
    isRecommendedCaptionLoading: false,
    hasMedia: false,
    shouldDisableModification: false,
    socialNetwork: SocialNetwork.Facebook,
  };
const defaultRecommendedHashtagsConfig: ISmartInputRecommendedHashtagsConfiguration =
  {
    isRecommendedAiHashtagsEnabled: false,
    isRecommendedAiHashtagsEnabledForPlanTier: false,
    isRecommendedRecentHashtagsEnabled: false,
  };
const defaultLinkShorteningConfig: ISmartInputLinkShorteningConfiguration = {
  isEnabled: false,
  getOriginalLink: (_shortLink) => Promise.resolve(undefined),
  getShortLink: (_originalLink) => Promise.resolve(undefined),
};
const defaultShowroomLinksConfig: ISmartInputShowroomLinksConfiguration = {
  isEnabled: false,
  getShowroomLink: (_addLinkToCaption) => _addLinkToCaption,
  getShowroomShortlink: (_showRoomShortLinkRequest) => Promise.resolve({}),
};
const defaultParseMethod = parseTwitterText;

/**
 * The SmartInput is a rich text input that enables rendering components within an
 * editable input. It allows us "smart" functionality like adding emojis, recommended
 * hashtags, and shortened links.
 *
 * NOTE: It is not a controlled input. It will render the initial value passed, but will
 * control its own input state after that point.
 *
 */
const SmartInput = React.forwardRef<
  {
    focus: (options?: FocusOptions) => void;
  },
  Props
>(
  (
    {
      className = '',
      emojiPicker = defaultEmojiPickerConfig,
      generativeAiCaption = defaultGenerativeAiCaptionConfig,
      generativeAiResponse = defaultGenerativeAiResponseConfig,
      handleBlur,
      handleEnterKeyDown,
      handleFocus,
      hasError = false,
      isDisabled = false,
      linkShortening = defaultLinkShorteningConfig,
      mentions = defaultAtMentionsConfig,
      numRows = 3,
      onChange,
      placeholder,
      recommendedCaption = defaultRecommendedCaptionConfig,
      recommendedHashtags = defaultRecommendedHashtagsConfig,
      showroomLinks = defaultShowroomLinksConfig,
      socialTextParseMethod = defaultParseMethod,
      trackingIdPrefix = '',
      value,
    },
    ref,
  ) => {
    const editorRef = useRef<Editor>(null);
    const prevPlainText = useRef<string>(value ?? '');

    const [editorState, setEditorState] = useState(() =>
      EditorState.createWithContent(ContentState.createFromText(value ?? '')),
    );
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [
      isRecommendedHashtagsDropdownOpen,
      setIsRecommendedHashtagsDropdownOpen,
    ] = useState(false);
    const [
      isMentionsSuggestionsDropdownOpen,
      setIsMentionsSuggestionsDropdownOpen,
    ] = useState(false);
    const [mentionsSuggestionsQuery, setMentionsSuggestionsQuery] =
      useState<IMentionsSuggestionsQuery>({
        blockKey: '',
        end: 0,
        queryString: '',
        start: 0,
      });

    const [isGenerativeAiCaptionModalOpen, setIsGenerativeAiCaptionModalOpen] =
      useState(false);

    const [isLinkShorteningModalOpen, setIsLinkShorteningModalOpen] =
      useState(false);
    const [
      linkShorteningModalConfiguration,
      setLinkShorteningModalConfiguration,
    ] = useState<ILinkShorteningModalConfiguration>({
      insertShortLink: (_shortLink: string) => {
        return Promise.resolve();
      },
      isEditingLink: false,
    });

    const [
      emojiConnectPopover,
      { closePopover: closeEmojiPopover, state: emojiPopoverState },
    ] = usePopover();

    /**
     * This function should always be used when updating the editorState otherwise the `onChange`
     * functionality can hit a race condition with the state update. Do not directly use `setEditorState`
     */
    const handleEditorStateChange = useCallback(
      (s: EditorState, shouldFocus?: boolean) => {
        const plainText = s.getCurrentContent().getPlainText();
        setEditorState(s);

        // Only call the onChange callback when the plainText value changes
        if (prevPlainText.current !== plainText) {
          onChange(plainText);
          prevPlainText.current = plainText;
        }

        if (
          mentions.isEnabled &&
          !(
            getIsCursorInMention(s, editorContext) ||
            getIsCursorAfterAtSymbol(s)
          )
        ) {
          // If mentions are enabled we want to close the dropdown anytime the cursor is outside of a mention
          handleSetMentionsSuggestionsDropdownIsOpen(false, false);
        }

        if (shouldFocus) {
          // Push the focus to the next tick to ensure focus doesn't happen before the click focus happens
          setTimeout(() => setIsFocused(true), 0);
        }
      },
      [onChange],
    );

    /**
     * Reset the SmartInput `editorState` from a plain text value
     *
     * @param value text string to create new `editorState` from
     */
    const resetEditorState = (value: string) => {
      const newEditorState = EditorState.createWithContent(
        ContentState.createFromText(value),
      );
      setEditorState(newEditorState);
    };

    /**
     * Handles closing and opening the mentions suggestions dropdown as well as refocusing the editor if closed
     *
     * @param shouldOpen
     */
    const handleSetMentionsSuggestionsDropdownIsOpen = (
      shouldOpen: boolean,
      shouldFocusEditor = false,
    ) => {
      setIsMentionsSuggestionsDropdownOpen(shouldOpen);

      if (shouldFocusEditor) {
        focusEditor();
      }
    };

    /**
     * Handles closing and opening the recommended hashtags dropdown as well as refocusing the editor if closed
     *
     * @param shouldOpen
     */
    const handleSetRecommendedHashtagsDropdownIsOpen = (
      shouldOpen: boolean,
      shouldFocusEditor = false,
    ) => {
      setIsRecommendedHashtagsDropdownOpen(shouldOpen);

      if (shouldFocusEditor) {
        focusEditor();
      }
    };

    const focusEditor = () => {
      editorRef.current?.focus();
    };

    useImperativeHandle(ref, () => ({
      focus: () => {
        focusEditor();
      },
    }));

    /**
     * Handles all keydown events that bubble from anywhere within the SmartInputWrapper
     *
     * @param e keydown event
     */
    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (keyEquals(e, KEYS.ESC)) {
        // prevent the native escape functionality
        e.preventDefault();
        handleCloseOpenPlugins();
      }
    };

    // Custom key binding function
    const keyBindingFn = (e: React.KeyboardEvent) => {
      if (handleEnterKeyDown) {
        if (
          keyEquals(e, KEYS.ENTER) &&
          !e.shiftKey &&
          !e.ctrlKey &&
          !e.altKey &&
          !e.metaKey
        ) {
          e.preventDefault(); // Prevent the native behavior
          return 'handle-enter';
        }
      }
      return getDefaultKeyBinding(e);
    };

    // Custom key command handling
    const handleKeyCommand = (command: string): DraftHandleValue => {
      if (command === 'handle-enter') {
        // Custom behavior for Enter key
        if (handleEnterKeyDown) {
          handleEnterKeyDown();
        }
        return 'handled'; // Indicate that the command was handled
      }
      // Allow draft-js to handle the command if not custom
      return 'not-handled';
    };

    /**
     * Handles closing any open SmartInput plugins
     */
    const handleCloseOpenPlugins = (shouldFocus = true) => {
      if (isRecommendedHashtagsDropdownOpen) {
        handleSetRecommendedHashtagsDropdownIsOpen(false, false);
      }

      if (isMentionsSuggestionsDropdownOpen) {
        handleSetMentionsSuggestionsDropdownIsOpen(false, false);
      }

      if (emojiPopoverState?.isVisible) {
        closeEmojiPopover();
      }

      if (shouldFocus) {
        focusEditor();
      }
    };

    /**
     * Handling clicks outside of Smart Input Wrapper to close all open plugins
     */
    const [{ elementRef: smartInputWrapperRef }, setIsActive] =
      useOutsideClick<HTMLDivElement>(() => {
        handleCloseOpenPlugins(false);
      });

    useEffect(() => {
      setIsActive(
        isRecommendedHashtagsDropdownOpen || isMentionsSuggestionsDropdownOpen,
      );
    }, [
      isMentionsSuggestionsDropdownOpen,
      isRecommendedHashtagsDropdownOpen,
      setIsActive,
    ]);

    const editorContext: ISmartInputContext = {
      editor: {
        focusEditor: () => focusEditor(),
        getEditorState: () => editorState,
        isFocused,
        resetEditorState: (value) => resetEditorState(value),
        setEditorState: (newEditorState, shouldFocus) =>
          handleEditorStateChange(newEditorState, shouldFocus),
        setReadOnly: (shouldReadOnly) => setIsReadOnly(shouldReadOnly),
      },
      emojiPicker: {
        isEnabled: emojiPicker.isEnabled,
        placement: emojiPicker.placement,
      },
      generativeAiCaption: {
        getGeneratedCaption: generativeAiCaption.getGeneratedCaption,
        isEnabled: generativeAiCaption.isEnabled,
        isEnabledForPlanTier: generativeAiCaption.isEnabledForPlanTier,
        isModalTriggerEnabled: generativeAiCaption.isModalTriggerEnabled,
        openModal: () => {
          setIsGenerativeAiCaptionModalOpen(true);
        },
        disabledTierTooltipContent:
          generativeAiCaption.disabledTierTooltipContent,
      },
      generativeAiResponse: {
        dropdownConfig: generativeAiResponse.dropdownConfig,
        getGeneratedResponse: (value: DropdownValue) =>
          generativeAiResponse.getGeneratedResponse(value),
        isEnabled: generativeAiResponse.isEnabled,
        isAiGenerativeResponseLoading:
          generativeAiResponse.isAiGenerativeResponseLoading,
        shouldDisableMenu: generativeAiResponse.shouldDisableMenu,
      },
      linkShortening: {
        isEnabled: linkShortening.isEnabled,
        getOriginalLink: linkShortening.getOriginalLink,
        getShortLink: linkShortening.getShortLink,
        openModal: (configuration: ILinkShorteningModalConfiguration) => {
          setLinkShorteningModalConfiguration(configuration);
          setIsLinkShorteningModalOpen(true);
        },
        additionalInfo: linkShortening.additionalInfo,
      },
      showroomLinks: {
        isEnabled: showroomLinks.isEnabled,
        getShowroomLink: (showroomLinkCallback: ShowroomLinkCallback) => {
          showroomLinks.getShowroomLink(showroomLinkCallback);
        },
        getShowroomShortlink: showroomLinks.getShowroomShortlink,
      },
      mentions: {
        isEnabled: mentions.isEnabled,
        getMentionsSuggestions: mentions.getMentionsSuggestions,
        getFacebookPageById: mentions.getFacebookPageById,
        isDropdownOpen: isMentionsSuggestionsDropdownOpen,
        setIsDropdownOpen: (
          shouldOpen: boolean,
          shouldFocusEditor?: boolean,
        ) => {
          handleSetMentionsSuggestionsDropdownIsOpen(
            shouldOpen,
            shouldFocusEditor,
          );
        },
        setMentionsSuggestionsQuery: (query: IMentionsSuggestionsQuery) => {
          setMentionsSuggestionsQuery(query);
        },
        isInstagramUserEmbedEnabled: !!mentions.isInstagramUserEmbedEnabled,
      },
      recommendedCaption: {
        getRecommendedCaption: recommendedCaption.getRecommendedCaption,
        isEnabled: recommendedCaption.isEnabled,
        isRecommendedCaptionLoading:
          recommendedCaption.isRecommendedCaptionLoading,
        hasMedia: recommendedCaption.hasMedia,
        shouldDisableModification: recommendedCaption.shouldDisableModification,
        socialNetwork: recommendedCaption.socialNetwork,
      },
      recommendedHashtags: {
        disabledText: recommendedHashtags.disabledText,
        disabledTierTooltipContent:
          recommendedHashtags.disabledTierTooltipContent,
        errorMessage: recommendedHashtags.errorMessage,
        getRecommendedAiHashtags: recommendedHashtags.getRecommendedAiHashtags,
        isDropdownOpen: isRecommendedHashtagsDropdownOpen,
        isRefetchingRecommendedAiHashtags:
          !!recommendedHashtags.isRefetchingRecommendedAiHashtags,
        isLoadingRecommendedAiHashtags:
          !!recommendedHashtags.isLoadingRecommendedAiHashtags,
        isRecommendedAiHashtagsEnabled:
          !!recommendedHashtags.isRecommendedAiHashtagsEnabled,
        isRecommendedAiHashtagsEnabledForPlanTier:
          recommendedHashtags.isRecommendedAiHashtagsEnabledForPlanTier,
        isRecommendedRecentHashtagsEnabled:
          !!recommendedHashtags.isRecommendedRecentHashtagsEnabled,
        recommendedAiHashtags: recommendedHashtags.recommendedAiHashtags,
        recommendedRecentHashtags:
          recommendedHashtags.recommendedRecentHashtags,
        setIsDropdownOpen: (
          shouldOpen: boolean,
          shouldFocusEditor?: boolean,
        ) => {
          handleSetRecommendedHashtagsDropdownIsOpen(
            shouldOpen,
            shouldFocusEditor,
          );
        },
      },
      socialTextParseMethod,
      trackingIdPrefix,
    };

    const findEntities = (
      contentBlock: ContentBlock,
      callback: (start: number, end: number) => void,
    ): void => {
      const text = contentBlock.getText();
      const parsedText = editorContext.socialTextParseMethod(text);

      parsedText.forEach((entity) => {
        if (
          isDefined(entity.start) &&
          isDefined(entity.end) &&
          // we need this because @draft-js-plugins/editor seems to recognize @ regardless of if
          // the entity type is Text and it messes with what is highlighted as a mention
          // i.e. without this, if text = '@a@b' it highlights both entities even though '@a' is parsed as an
          // InstagramMention and '@b' as Text
          (!editorContext.mentions?.isInstagramUserEmbedEnabled ||
            entity.type !== SocialTextEntityType.Text ||
            !entity.value.startsWith('@'))
        ) {
          callback(entity.start, entity.end);
        }
      });
    };

    // decorators are order dependent
    const decorators = [
      ...(!!recommendedHashtags.isRecommendedRecentHashtagsEnabled ||
      !!recommendedHashtags.isRecommendedAiHashtagsEnabled
        ? recommendedHashtagsDecorators
        : []),
      {
        strategy: findEntities,
        component: DecoratedText,
      },
      ...(mentions.isEnabled ? mentionsSuggestionsDecorators : []),
    ];

    return (
      <SmartInputContextProvider value={editorContext}>
        <SmartInputWrapper
          ref={smartInputWrapperRef}
          className={className}
          onKeyDown={handleKeyDown}
          tabIndex={-1}
        >
          <EditorWrapper
            actionBarHeight={ACTION_BAR_HEIGHT}
            hasError={hasError}
            isDisabled={isDisabled}
            isFocused={isFocused}
            isHovered={isHovered}
            numRows={numRows}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <Editor
              ref={editorRef}
              decorators={decorators}
              editorState={editorState}
              handleKeyCommand={handleKeyCommand}
              keyBindingFn={keyBindingFn}
              onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                setIsFocused(false);
                if (handleBlur) {
                  handleBlur(e);
                }
              }}
              onChange={handleEditorStateChange}
              onFocus={() => {
                setIsFocused(true);
                if (handleFocus) {
                  handleFocus();
                }
              }}
              placeholder={placeholder}
              readOnly={isDisabled || isReadOnly}
              stripPastedStyles={true} // this preserves line breaks when copy+pasting text from browsers, slack, etc
            />
            <SmartInputActionBar
              emojiConnectPopover={emojiConnectPopover}
              isDisabled={isDisabled}
              setIsFocused={setIsFocused}
            />
          </EditorWrapper>
          {mentions.isEnabled &&
            isMentionsSuggestionsDropdownOpen &&
            !emojiPopoverState?.isVisible && (
              <MentionsSuggestionsDropdown query={mentionsSuggestionsQuery} />
            )}
          {(!!recommendedHashtags.isRecommendedRecentHashtagsEnabled ||
            !!recommendedHashtags.isRecommendedAiHashtagsEnabled) &&
            isRecommendedHashtagsDropdownOpen && <HashtagDropdown />}
        </SmartInputWrapper>
        {generativeAiCaption.isEnabled && isGenerativeAiCaptionModalOpen && (
          <GenerativeAiCaptionModal
            closeModal={() => {
              setIsGenerativeAiCaptionModalOpen(false);
            }}
            isOpen={isGenerativeAiCaptionModalOpen}
          />
        )}
        {linkShortening.isEnabled && isLinkShorteningModalOpen && (
          <LinkShorteningModal
            closeModal={() => {
              setIsLinkShorteningModalOpen(false);
            }}
            handleInsertShortLink={
              linkShorteningModalConfiguration.insertShortLink
            }
            initialOriginalLinkValue={
              linkShorteningModalConfiguration.initialOriginalLinkValue
            }
            initialShortLinkValue={
              linkShorteningModalConfiguration.initialShortLinkValue
            }
            isEditLinkModal={linkShorteningModalConfiguration.isEditingLink}
            isOpen={isLinkShorteningModalOpen}
          />
        )}
      </SmartInputContextProvider>
    );
  },
);

SmartInput.displayName = 'SmartInput';

const SmartInputWrapper = styled.div`
  position: relative;
  outline: none;

  &:focus {
    outline: none;
  }
`;

/**
 * The styling below mirrors the styling of the `cu-form-control` class as found in Unison. The styles are manually
 * applied to mimic the appearance of the form control even though this wrapper div is not actually the editable
 * element.
 */
const EditorWrapper = styled.div<{
  actionBarHeight: number;
  hasError: boolean;
  isDisabled: boolean;
  isFocused: boolean;
  isHovered: boolean;
  numRows: number;
}>(
  ({
    actionBarHeight,
    hasError,
    isDisabled,
    isFocused,
    isHovered,
    numRows,
    theme,
  }) => `
  position: relative;
  // Calculate our height with 1.5em line-height + vertical borders + vertical padding
  height: calc((1.5em * ${numRows}) + (${theme.spacings[2]} * 2) + (${
    theme.borderWidths[1]
  } * 2) + ${actionBarHeight}px) !important;
  overflow-y: auto;
  border-radius: ${theme.borderRadii[1]};
  border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
  outline: none;

  .DraftEditor-root {
    height: calc(100% - ${actionBarHeight}px);
    padding: ${theme.spacings[2]};
    overflow-y: auto;
    overflow-x: hidden;
  }

  // Ensure that the child of the root fills space to enable focus UX
  .DraftEditor-editorContainer {
    height: 100%;
  }

  .public-DraftEditor-content {
    height: 100%;

    // Safari Bug where cursor doesn't appear without a border https://github.com/facebook/draft-js/issues/2100#issuecomment-765300957
    border: 1px solid ${getRgbaForThemeHex(theme.colors.white, 0.01)};
  }

  .public-DraftEditorPlaceholder-inner {
    height: 100%;
    color: ${theme.colors.aliases.disabled};
    padding: 1px;
    opacity: 1;
  }

  ${
    isHovered && !isFocused && !isDisabled
      ? `box-shadow: ${theme.boxShadows[1]};`
      : ''
  }


  ${
    hasError && !isDisabled
      ? `
    border-color: ${
      isFocused
        ? `${theme.borderWidths[1]} solid ${theme.colors.gray2}`
        : theme.colors.aliases.error
    };
    background-color: ${theme.colors.red0};
  `
      : ''
  }


  ${
    isDisabled
      ? `
    background-color: ${theme.colors.gray0};
    color: ${theme.colors.aliases.disabled};
    cursor: not-allowed;
  `
      : ''
  }

  ${
    // Use a pseudo-element to maintain the inset box-shadow when there are children that are not transparent
    isFocused && !isDisabled
      ? `
    &::after {
      content: '';
      position: absolute;
      top:0;
      left: 0;
      bottom: 0;
      right: 0;
      border-radius: ${theme.borderRadii[1]};
      border: ${theme.borderWidths[1]} solid ${theme.colors.cyan8};
      box-shadow: inset 0 0 0 ${theme.borderWidths[1]} ${theme.colors.cyan8}, inset 0 0 0 ${theme.borderWidths[2]} ${theme.colors.cyan2};
      outline: none;
  
      // Allow clicks to pass through to parent container
      pointer-events: none;
    }
    `
      : ''
  }
  }
`,
);

export default SmartInput;
